import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { AureloLogo } from "@/components/Logo";
import { DevelopedBy } from "@/components/CodeLabsLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles, ArrowRight, Loader2, XCircle } from "lucide-react";

const AuthShell = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="flex items-center justify-center p-6">
      <div className="w-full max-w-sm">{children}</div>
    </div>
    <div className="hidden lg:flex relative bg-[#0B102B] items-center justify-center p-12 overflow-hidden">
      <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full aurelo-gradient opacity-30 blur-3xl" />
      <div className="relative text-white max-w-md">
        <AureloLogo size={32} light />
        <h2 className="text-3xl font-extrabold mt-8 tracking-tight leading-tight">You've been invited to join a team on Aurelo.</h2>
        <p className="text-slate-300 mt-3">Set a password below to activate your account and start collaborating.</p>
        <div className="mt-10 pt-6 border-t border-white/10"><DevelopedBy labelColor="#64748B" height={22} /></div>
      </div>
    </div>
  </div>
);

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { acceptInvite } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (!token) { setError("This invite link is missing a token."); setChecking(false); return; }
    api.get(`/auth/invite/${token}`).then(({ data }) => setInvite(data))
      .catch((err) => setError(err?.response?.data?.detail || "This invite link is invalid or has expired."))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      await acceptInvite(token, password);
      toast.success(`Welcome to ${invite?.tenant_name}!`);
      navigate("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't accept this invite");
    } finally { setLoading(false); }
  };

  if (checking) {
    return <AuthShell><div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div></AuthShell>;
  }

  if (error) {
    return (
      <AuthShell>
        <div className="lg:hidden mb-8"><AureloLogo size={30} /></div>
        <div data-testid="invite-error" className="flex flex-col items-center text-center py-6">
          <XCircle className="h-10 w-10 text-red-400 mb-3" />
          <h1 className="text-xl font-bold tracking-tight">Invite link issue</h1>
          <p className="text-sm text-slate-500 mt-1.5">{error}</p>
          <Link to="/login" className="text-sm font-semibold text-[#7C3AED] hover:underline mt-5">Go to sign in</Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="lg:hidden mb-8"><AureloLogo size={30} /></div>
      <h1 className="text-2xl font-bold tracking-tight">Join {invite?.tenant_name}</h1>
      <p className="text-sm text-slate-500 mt-1">
        {invite?.name} ({invite?.email}) — set a password to activate your {invite?.role} account.
      </p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div><Label>New password</Label><Input data-testid="accept-invite-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" minLength={8} required /></div>
        <div><Label>Confirm password</Label><Input data-testid="accept-invite-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5" minLength={8} required /></div>
        <Button type="submit" data-testid="accept-invite-submit" className="w-full aurelo-gradient text-white" disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" />Activate My Account<ArrowRight className="h-4 w-4 ml-1" /></>}
        </Button>
      </form>
    </AuthShell>
  );
}
